/**
 * The right half of the queue page: the queue's scores, one numbered step
 * each, with the score's guideline under it and big tap targets — Pass / Fail
 * in green / red, a scale with its end labels, categories as chips. Then an
 * optional comment, and the corrected output, folded away until asked for and
 * opened pre-filled with the agent's answer. Every score needs an answer.
 */

import { useId } from "react";
import { LabelMark } from "@/components/ui/field";
import { Check, ThumbsDown, ThumbsUp } from "lucide-react";
import { CorrectedOutput } from "./corrected-output";
import type { Answers, ScoreConfig } from "./types";

/** Scores still to answer before the item can be completed. */
export const unanswered = (scores: ScoreConfig[], answers: Answers) => scores.filter((s) => !answers[s.id]);

const choice = "rounded-lg border text-[13px] font-medium transition-colors focus-visible:outline-2";
const idle = "border-input bg-surface text-body hover:border-dim hover:bg-hover";
const on = "border-primary bg-primary text-on-primary";

type Pick = { value: string; onChange: (v: string) => void };

function PassFail({ value, onChange }: Pick) {
  const options = [
    { key: "pass", label: "Pass", Icon: ThumbsUp, tone: "border-success bg-success-bg text-success" },
    { key: "fail", label: "Fail", Icon: ThumbsDown, tone: "border-err bg-err-bg text-err" },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(({ key, label, Icon, tone }) => (
        <button
          key={key}
          type="button"
          aria-pressed={value === key}
          onClick={() => onChange(value === key ? "" : key)}
          className={`${choice} flex items-center justify-center gap-2 py-2.5 ${value === key ? tone : idle}`}
        >
          <Icon size={15} strokeWidth={2} aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
}

function Scale({ value, onChange, score: s }: Pick & { score: ScoreConfig }) {
  const steps = Array.from({ length: s.scale_max - s.scale_min + 1 }, (_, i) => String(s.scale_min + i));
  const [low, high] = s.scale_labels;
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
      >
        {steps.map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={value === n}
            onClick={() => onChange(value === n ? "" : n)}
            className={`${choice} py-2 font-mono ${value === n ? on : idle}`}
          >
            {n}
          </button>
        ))}
      </div>
      {(low || high) && (
        <div className="flex justify-between text-[11px] text-faint">
          <span>{low && `${s.scale_min} · ${low}`}</span>
          <span>{high && `${s.scale_max} · ${high}`}</span>
        </div>
      )}
    </div>
  );
}

function Chips({ value, onChange, options }: Pick & { options: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={value === o}
          onClick={() => onChange(value === o ? "" : o)}
          className={`${choice} rounded-full! px-3 py-1.5 ${value === o ? on : idle}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function ScoreStep({ step, score: s, value, onChange }: Pick & { step: number; score: ScoreConfig }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={`${id}-name`} className="flex gap-3">
      <span
        aria-hidden
        className={`mt-px grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
          value ? "bg-success text-on-primary" : "bg-chip text-chip-ink"
        }`}
      >
        {value ? <Check size={12} strokeWidth={3} /> : step}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span id={`${id}-name`} className="text-[13px] font-semibold">
            {s.name}
            <LabelMark required />
          </span>
          {s.description && <span className="text-xs text-muted">{s.description}</span>}
        </div>
        {s.kind === "pass_fail" && <PassFail value={value} onChange={onChange} />}
        {s.kind === "scale" && <Scale value={value} onChange={onChange} score={s} />}
        {s.kind === "categories" && <Chips value={value} onChange={onChange} options={s.categories} />}
      </div>
    </div>
  );
}

export function AnnotationForm({
  scores,
  answers,
  onAnswer,
  comment,
  onComment,
  corrected,
  onCorrected,
  agentOutput,
  expectedOutput,
}: {
  scores: ScoreConfig[];
  answers: Answers;
  onAnswer: (scoreId: string, value: string) => void;
  comment: string;
  onComment: (value: string) => void;
  /** The corrected answer itself; `null` when the reviewer gave none. */
  corrected: unknown;
  onCorrected: (value: unknown) => void;
  /** Pre-fills the corrected output, so the reviewer edits instead of retyping. */
  agentOutput: unknown;
  /** Offered as another place to start the corrected answer. */
  expectedOutput: unknown;
}) {
  return (
    <div className="flex flex-col gap-5">
      {scores.map((s, i) => (
        <ScoreStep
          key={s.id}
          step={i + 1}
          score={s}
          value={answers[s.id] ?? ""}
          onChange={(v) => onAnswer(s.id, v)}
        />
      ))}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="review-comment" className="text-[13px] font-semibold">
          Comment
          <LabelMark optional />
        </label>
        <textarea
          id="review-comment"
          className="inp min-h-[56px]!"
          placeholder="Anything the team should know about this item"
          value={comment}
          onChange={(e) => onComment(e.target.value)}
        />
      </div>
      <CorrectedOutput
        value={corrected}
        onChange={onCorrected}
        agentOutput={agentOutput}
        expectedOutput={expectedOutput}
      />
    </div>
  );
}
